<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { BookOpen, FolderCog, Plus, RefreshCw } from '@lucide/vue'
import { Button, InlineAlert, ResponsiveActionMenu, StateCard, useConfirm, useToast } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from '../stores/library'
import { useCloudLibraryStore } from '../stores/cloud-library'
import { useLibraryEntriesStore } from '../stores/library-entries'
import { useBackupQueueStore } from '../stores/backup-queue'
import { controlSizeFor, provideLibraryLayout } from '../lib/library-layout'
import { useLibraryDisplay } from '../lib/library-preferences'
import { type BulkOutcome } from '../lib/bulk-progress'
import { libraryShortcut } from '../lib/library-shortcuts'
import { shortcutsBlocked } from '../lib/reader-shortcuts'
import { removeEntries } from '../lib/bulk-removal'
import type { LibraryEntry } from '../lib/library-entries'
import { useEntryReader } from '../lib/entry-reader'
import LibraryShell from '../components/library/library-shell.vue'
import LibraryToolbar from '../components/library/library-toolbar.vue'
import LibrarySourcesPanel from '../components/library/library-sources-panel.vue'
import LibrarySourcesDialog from '../components/library/library-sources-dialog.vue'
import RelinkComicDialog from '../components/library/relink-comic-dialog.vue'
import { useSourceRelocation } from '../lib/source-relocation'
import type { CollectionCell } from '../lib/collection-presentation'
import ComicGrid from '../components/library/comic-grid.vue'
import ComicList from '../components/library/comic-list.vue'
import SelectionActionBar from '../components/library/selection-action-bar.vue'
import BulkCategoryDialog from '../components/library/bulk-category-dialog.vue'
import CollectionPickerDialog from '../components/library/collection-picker-dialog.vue'
import CollectionEditDialog from '../components/library/collection-edit-dialog.vue'
import { useCollectionActions } from '../lib/collection-actions'
import { collectionOpenTarget } from '../lib/collection-open-target'
import { useComicRemoval } from '../lib/comic-removal-flow'
import { errorMessage } from '../lib/fs-access'
import { deleteCollectionsWithMembers, skippedCollectionsNotice } from '../lib/collection-bulk'
import type { CollectionSummary } from '../lib/api-client'
import BulkEditDialog from '../components/library/bulk-edit-dialog.vue'
import AddComicsDialog from '../components/library/add-comics-dialog.vue'
import ComicMetadataDialog from '../components/library/comic-metadata-dialog.vue'
import CloudComicMetadataDialog from '../components/library/cloud-comic-metadata-dialog.vue'
import CatalogRegistrationPanel from '../components/library/catalog-registration-panel.vue'
import BackupQueueBar from '../components/library/backup-queue-bar.vue'
import ComicInfoDialog from '../components/library/comic-info-dialog.vue'
import UploadPanel from '../components/library/upload-panel.vue'
import SourceChoicePanel from '../components/library/source-choice-panel.vue'

const local = useLibraryStore()
const cloud = useCloudLibraryStore()
const entries = useLibraryEntriesStore()
const backup = useBackupQueueStore()
const compact = provideLibraryLayout()
const controlSize = computed(() => controlSizeFor(compact.value))
const router = useRouter()
const toast = useToast()
const display = useLibraryDisplay()
const listView = computed(() => display.viewMode.value === 'list')
const { confirm } = useConfirm()
const bulkCategories = ref(false)
const collecting = ref(false)
const editingCollection = ref<CollectionSummary>()
const collectionActions = useCollectionActions()
const { removeComic } = useComicRemoval()
const bulkEditing = ref(false)
// 部分完成要留在畫面上，因此用 inline alert 而非 toast（toast 會被下一則覆蓋）。
const bulkNotice = ref('')
const adding = ref(false)
const filterOpen = ref(false)
const managingSources = ref(false)
const editingLocal = ref<Comic>()
const editingCloud = ref<Comic>()
const inspecting = ref<LibraryEntry>()
const uploading = ref<Comic>()
// 閱讀入口與集合頁共用同一份來源選擇規則。
const { read, choosingSource, chooseSource } = useEntryReader()
const relinking = ref<Comic>()
const sourceRelocation = useSourceRelocation(message => toast.error('無法重新指向', { description: message }))

/*
  書庫只有一份清單。加入作品是桌機的主要動作；不支援本地檔案的裝置沒有東西可加，
  那裡的主要動作是把雲端清單重新取得一次。
*/
const actionLabel = computed(() => {
  if (!local.supported) return cloud.refreshing ? '更新中…' : '更新雲端'
  return local.busy ? '處理中…' : compact.value ? '加入' : '加入作品'
})
const actionDisabled = computed(() => local.supported
  ? !local.ready || local.busy
  : !cloud.ready || cloud.refreshing)
const refreshing = computed(() => local.refreshing || cloud.refreshing)
const secondaryActions = computed(() => [
  { key: 'refresh', label: refreshing.value ? '正在重整…' : '重整書庫', icon: RefreshCw, disabled: refreshing.value },
  ...(local.supported ? [{ key: 'sources', label: '管理來源', icon: FolderCog, disabled: !local.hasSources }] : []),
])
/** 雲端清單取不到，但本機那一份還在：清單照顯示，另外講一句。 */
const cloudUnavailable = computed(() => !!cloud.error && cloud.ready)
const sourceLine = computed(() => local.supported
  ? local.hasSources ? `${local.libraries.length} 個來源 · D1 metadata · R2 圖片` : '尚未加入來源'
  : 'D1 metadata · R2 圖片')

/**
 * 多選的快捷鍵。焦點在文字輸入或對話框內時完全不作用——那個判斷與閱讀器共用同一份，
 * 寫錯的後果是在搜尋框打字會觸發全選。
 */
function keydown(event: KeyboardEvent) {
  if (shortcutsBlocked(event.target)) return
  const match = libraryShortcut(event.key, {
    ctrl: event.ctrlKey, meta: event.metaKey, alt: event.altKey, shift: event.shiftKey,
  }, entries.selectionMode)
  if (!match) return
  if (match.action === 'select-all') entries.selectAllFiltered()
  else entries.exitSelectionMode()
  if (match.preventDefault) event.preventDefault()
}

onMounted(async () => {
  window.addEventListener('keydown', keydown)
  await entries.initialize()
})
onBeforeUnmount(() => window.removeEventListener('keydown', keydown))

/**
 * 書庫內容變動就把佇列補齊。只排程、不執行——實際開始需要一次使用者手勢，
 * 因為未授權的來源要靠 requestPermission() 才讀得到，而它必須在手勢的呼叫堆疊內。
 */
const backupSignature = computed(() => local.comics
  .map(comic => `${comic.id}:${comic.indexed ? 1 : 0}:${comic.syncState}`)
  .join('|'))
watch(() => local.ready && backupSignature.value, ready => {
  // ready 之前 comics 還是空陣列，這時排程會把持久化的佇列整份清掉。
  if (ready === false) return
  void backup.sync()
}, { immediate: true })

async function primaryAction() {
  if (actionDisabled.value) return
  if (!local.supported) { await cloud.refresh(true); return }
  // 兩個匯入入口都在對話框裡；原生選擇器由該處的按鈕直接觸發。
  adding.value = true
}
function clearFilters() {
  entries.clearFilters()
  window.scrollTo({ top: 0, behavior: 'instant' })
}
async function secondaryAction(key: string) {
  if (key === 'sources') { managingSources.value = true; return }
  await entries.refresh()
}
function openUpload(comic: Comic) { editingLocal.value = undefined; uploading.value = comic }
/** 多選的備份：整批附加到佇列尾端並開始，不開任何對話框。 */
async function backupSelected() {
  const targets = entries.backupTargets
  const collectionNotice = skippedCollectionsNotice(entries.skippedCollectionCount)
  const skipped = entries.selectedEntries.length - targets.length
  entries.exitSelectionMode()
  if (!targets.length) return
  await backup.enqueue(targets)
  const skippedComics = skipped ? `另外 ${skipped} 本跳過（不在這台裝置上，或已經備份完成）。` : ''
  bulkNotice.value = skippedComics || collectionNotice ? `已把 ${targets.length} 本加入備份佇列。${skippedComics}${collectionNotice}` : ''
}
/**
 * 卡片選單的「更新位置」。單本匯入的作品其資料夾就是來源，直接走來源層級——那裡會開啟
 * 瀏覽器的資料夾選擇器，因此這一段在它之前不能有 await；根來源底下的作品開候選對話框。
 */
function relink(entry: LibraryEntry) {
  if (!entry.local) return
  const source = local.sourceOf(entry.local)
  if (source?.kind === 'comic') { void sourceRelocation.relocate(source); return }
  relinking.value = entry.local
}
function relinked(comic: Comic) {
  toast.success('已更新作品位置', { description: comic.title })
}
/** 卡片選單的備份：插到佇列最前面並開始，不開任何對話框。 */
async function backupNow(entry: LibraryEntry) {
  editingLocal.value = undefined
  if (!entry.local) return
  await backup.enqueue([entry.local], true)
}
async function closeUpload() {
  const comicId = uploading.value?.id
  uploading.value = undefined
  await nextTick()
  if (comicId) document.querySelector<HTMLElement>(`.comic-manage[data-comic-id="${CSS.escape(comicId)}"]`)?.focus()
}
/**
 * 開啟一本作品。
 *
 * 來源自動選，因為單一清單裡沒有「我剛才點的是哪一頁」可以當作表態：本機那一份可用
 * 且頁數與雲端相同就讀本機（省流量），否則讀雲端。兩邊頁數不同才問——那時兩份都真的
 * 讀得出內容，該由使用者決定。
 */
/** 加入集合完成：未註冊而被擋下的作品以 inline alert 說明，其餘已進集合；離開多選。 */
function collected(message: string) {
  bulkNotice.value = message
  entries.exitSelectionMode()
}
/** 集合格在非多選模式下的點擊：開集合頁，成員在那裡各自閱讀。 */
async function openCollection(cell: CollectionCell) {
  await router.push(`/collections/${encodeURIComponent(cell.collection.id)}`)
}
/** 合併模式：先逐話檢查可讀性，任一話讀不到就擋在書庫點名，不進閱讀器才失敗。 */
async function readCollection(cell: CollectionCell) {
  const target = collectionOpenTarget(cell.collection, { comics: local.comics, libraries: local.libraries, isAuthorized: local.isAuthorized })
  if (target.kind === 'blocked') { toast.warning('這本合併冊目前無法閱讀', { description: target.message }); return }
  await router.push(target.path)
}
async function dissolveCollection(cell: CollectionCell) {
  bulkNotice.value = ''
  await collectionActions.dissolve(cell.collection)
}
async function deleteCollection(cell: CollectionCell) {
  bulkNotice.value = ''
  await collectionActions.deleteWithMembers(cell.collection)
}
function reauthenticate() { window.location.assign(window.location.href) }
/** 整理的真相跟著作品走：已註冊的寫雲端目錄，尚未註冊的只有本機那一份。 */
function organize(entry: LibraryEntry) {
  inspecting.value = undefined
  if (entry.local && !entry.local.cloudComicId) { editingLocal.value = entry.local; return }
  if (entry.local) { editingLocal.value = entry.local; return }
  editingCloud.value = entry.comic
}

/** skippedCollections 為真時，動作只作用在作品格，跳過的集合數要講出來。 */
function reportBulk(outcome: BulkOutcome, verb: string, skippedCollections = 0) {
  entries.dropFromSelection(outcome.completedIds)
  // 批次動作也會寫到雲端目錄，因此雲端那份快取一定過期了。
  cloud.invalidate()
  const skipped = skippedCollectionsNotice(skippedCollections)
  if (outcome.reason) {
    bulkNotice.value = `已${verb} ${outcome.completed} 項後停止：${outcome.reason}${skipped}`
    return
  }
  bulkNotice.value = skipped
  if (!skipped) toast.success(`已${verb} ${outcome.completed} 項`)
}

/** 卡片與列表列的「刪除作品」：與整理對話框同一條確認與實作。 */
async function removeOne(entry: LibraryEntry) {
  bulkNotice.value = ''
  try {
    if (await removeComic(entry.local ?? entry.comic)) cloud.invalidate()
  } catch (cause) { toast.error('作品尚未移除', { description: errorMessage(cause) }) }
}
async function removeSelected() {
  const selected = entries.selectedEntries
  const collections = entries.selectedCollections
  if (!selected.length && !collections.length) return
  bulkNotice.value = ''
  /*
    刪除的對象由每一列實際持有的副本決定：這台裝置上有的那些只從書庫移除，雲端副本保留
    （要連雲端一起刪請逐本從整理作品進行）；不在這台裝置上的那些只有雲端一份，刪除就是
    刪那一份。說明文字必須逐字對應這個行為——這裡沒有復原。
  */
  const localCount = selected.filter(entry => entry.local).length
  const cloudOnlyCount = selected.length - localCount
  const removedFromDevice = `${localCount} 本只從這台裝置的書庫移除，硬碟上的資料夾與壓縮檔不受影響，雲端副本也保留。`
  const removedFromCloud = `${cloudOnlyCount} 本不在這台裝置上，將刪除它們的雲端副本，包含圖片、閱讀進度與書籤。`
  // 選取裡的集合是「連同成員刪除」，與集合格選單那條路徑同一個實作；本數另外講清楚。
  const collectionMembers = collections.reduce((sum, item) => sum + item.memberCount, 0)
  const removedCollections = collections.length
    ? `另有 ${collections.length} 個集合會連同其成員一起刪除（共 ${collectionMembers} 本）。`
    : ''
  const comicPart = selected.length ? (localCount && cloudOnlyCount ? `${removedFromDevice}${removedFromCloud}` : cloudOnlyCount ? removedFromCloud : removedFromDevice) : ''
  await confirm({
    title: selected.length && collections.length
      ? `刪除選取的 ${selected.length} 本作品與 ${collections.length} 個集合？`
      : collections.length ? `刪除選取的 ${collections.length} 個集合？` : `刪除選取的 ${selected.length} 本作品？`,
    description: `${comicPart}${removedCollections}`,
    tone: 'danger',
    confirmLabel: `刪除 ${selected.length + collections.length} 項`,
    pendingLabel: '正在刪除…',
    onConfirm: async () => {
      const comicsOutcome = selected.length
        ? await removeEntries(selected, { removeLocal: id => local.deleteComic(id), deleteCloud: ids => cloud.deleteComics(ids) })
        : { completed: 0, completedIds: [] as string[], reason: '' }
      const collectionsOutcome = comicsOutcome.reason || !collections.length
        ? { completed: 0, completedIds: [] as string[], reason: '' }
        : await deleteCollectionsWithMembers(collections, id => collectionActions.deleteWithMembersNow(id, collections.find(item => item.id === id)?.members ?? []))
      reportBulk({
        completed: comicsOutcome.completed + collectionsOutcome.completed,
        completedIds: [...comicsOutcome.completedIds, ...collectionsOutcome.completedIds.map(id => `collection:${id}`)],
        reason: comicsOutcome.reason || collectionsOutcome.reason,
      }, '刪除')
    },
  })
}
</script>

<template>
  <LibraryShell>
    <header class="library-heading">
      <div class="library-heading-title"><h1>書庫</h1><span class="total-count">{{ entries.entries.length }} 本</span></div>
      <div class="library-heading-actions">
        <ResponsiveActionMenu plain icon-only :mode="compact ? 'sheet' : 'dropdown'" button-label="書庫選項" title="書庫選項" sheet-description="重新取得本機與雲端的作品資料。" :actions="secondaryActions" @select="secondaryAction" />
        <!-- 主要動作固定在標題列右上角。放進底部導覽會與兩個目的地混在一起，
             而它不是目的地；右上角在各種寬度下都是同一個位置。 -->
        <Button color="primary" :size="controlSize" :disabled="actionDisabled" @click="primaryAction"><component :is="local.supported ? Plus : RefreshCw" :size="16" aria-hidden="true" />{{ actionLabel }}</Button>
      </div>
    </header>
    <!--
      書庫只有一份清單：一本作品在哪裡讀得到是那一本的狀態，不是一個分頁。不支援本地
      檔案的裝置因此不需要特例——它的那一半本來就是空的，聯集之後只剩雲端那些。
    -->
    <div class="library-scope-row"><span>{{ sourceLine }}</span></div>
    <LibraryToolbar v-model:filter-open="filterOpen" />
    <SelectionActionBar
      v-if="entries.selectionMode"
      :backup-count="entries.backupTargets.length"
      @categories="bulkCategories = true"
      @collection="collecting = true"
      @edit="bulkEditing = true"
      @upload="backupSelected"
      @remove="removeSelected"
    />
    <InlineAlert v-if="bulkNotice" tone="warning" dismissible class="my-4" @dismiss="bulkNotice = ''">{{ bulkNotice }}</InlineAlert>
    <!-- 分類與整理的真相在雲端目錄，登入失效時只有變更受影響，瀏覽與本地閱讀照常。 -->
    <InlineAlert v-if="cloud.authenticationRequired && local.comics.length" tone="warning" class="my-5">
      <span>雲端登入已失效。書庫與本地閱讀不受影響，但分類與作品整理暫時無法變更。</span>
      <Button plain :size="controlSize" class="ml-2" @click="reauthenticate">重新登入</Button>
    </InlineAlert>
    <!-- 雲端清單取不到不讓整頁空白：書庫裡有哪些作品的資料就在本機，沒有理由被網路擋住。 -->
    <InlineAlert v-else-if="cloudUnavailable" tone="warning" class="my-5">
      <span>雲端清單無法載入，目前只顯示這台裝置上的作品。{{ cloud.error }}</span>
      <Button plain :size="controlSize" class="ml-2" @click="entries.refresh()">重試</Button>
    </InlineAlert>
    <CatalogRegistrationPanel />
    <InlineAlert v-if="local.error" tone="error" class="my-5">{{ local.error }}</InlineAlert>
    <StateCard v-if="!entries.ready" title="正在讀取書庫" message="讀取這台裝置上的作品，並取得雲端目錄…" :actionable="false" />
    <StateCard v-else-if="cloud.authenticationRequired && !local.comics.length" title="需要登入私人雲端" message="登入已失效；重新登入後會回到書庫。" action-label="重新登入" @action="reauthenticate" />
    <template v-else>
      <StateCard v-if="!local.supported && !entries.shelf.length && !entries.filtering" title="雲端書庫目前是空的" message="請先在支援本地檔案的桌面瀏覽器加入並備份作品。" :actionable="false" />
      <section v-else-if="local.supported && !local.hasSources && !entries.shelf.length" class="welcome-shelf">
        <StateCard class="welcome-state" title="加入本地漫畫" message="可以選一個漫畫根目錄再勾選要加入的作品，也可以直接匯入單獨一本作品的資料夾。兩個入口都在「加入作品」裡。" :actionable="false"><template #icon><BookOpen :size="42" :stroke-width="1.2" aria-hidden="true" /></template></StateCard>
        <p class="welcome-footnote">根目錄範例：漫畫／作品名稱／1.jpg。只讀取，不搬動；上傳須由作品整理面板明確開始。</p>
      </section>
      <template v-else>
        <div class="shelf-label"><h2>{{ entries.filtering ? '篩選結果' : '全部作品' }}</h2><span role="status" aria-live="polite">{{ entries.entries.length }} 本</span></div>
        <!-- 以格子數判斷而不是作品數：搜尋只比對到集合標題時作品清單是空的，但集合格要出現。 -->
        <template v-if="entries.shelf.length">
          <ComicList v-if="listView" :items="entries.shelf" @info="inspecting = $event" @edit="organize" @read="read" @backup="backupNow" @relink="relink" @remove="removeOne" @open-collection="openCollection" @read-collection="readCollection" @edit-collection="editingCollection = $event.collection" @dissolve-collection="dissolveCollection" @delete-collection="deleteCollection" />
          <ComicGrid v-else :items="entries.shelf" @info="inspecting = $event" @edit="organize" @read="read" @backup="backupNow" @relink="relink" @remove="removeOne" @open-collection="openCollection" @read-collection="readCollection" @edit-collection="editingCollection = $event.collection" @dissolve-collection="dissolveCollection" @delete-collection="deleteCollection" />
        </template>
        <StateCard v-else-if="entries.filtering" title="沒有符合的作品" message="請調整書名、作者或分類條件。" action-label="清除篩選" @action="clearFilters" />
        <StateCard v-else title="尚未加入作品" message="按「加入作品」選擇根資料夾中的作品，不會自動加入。" :actionable="false" />
        <LibrarySourcesPanel v-if="local.supported && local.hasSources" class="mt-6" />
      </template>
    </template>

    <template #bottom-bar>
      <BackupQueueBar @inspect="openUpload" />
    </template>

    <template #overlays>
      <ComicInfoDialog :entry="inspecting" @close="inspecting = undefined" @organize="organize" />
      <AddComicsDialog :open="adding" @close="adding = false" />
      <ComicMetadataDialog :comic="editingLocal" @close="editingLocal = undefined" />
      <CloudComicMetadataDialog :comic="editingCloud" @close="editingCloud = undefined" />
      <UploadPanel :comic="uploading" @close="closeUpload" />
      <SourceChoicePanel :comic="choosingSource?.comic" :cloud-page-count="choosingSource?.cloudPageCount" @close="choosingSource = undefined" @choose="chooseSource" />
      <LibrarySourcesDialog :open="managingSources" @close="managingSources = false" />
      <RelinkComicDialog :comic="relinking" @close="relinking = undefined" @done="relinked" />
      <BulkCategoryDialog
        :open="bulkCategories"
        @close="bulkCategories = false"
        @done="(outcome, mode) => reportBulk(outcome, mode === 'add' ? '加入分類' : '移除分類')"
      />
      <CollectionPickerDialog :open="collecting" @close="collecting = false" @done="collected" />
      <CollectionEditDialog :collection="editingCollection" @close="editingCollection = undefined" />
      <BulkEditDialog
        :open="bulkEditing"
        @close="bulkEditing = false"
        @done="(outcome, field) => reportBulk(outcome, field === 'title' ? '改名' : '改作者', entries.skippedCollectionCount)"
      />
    </template>
  </LibraryShell>
</template>
